<script lang="ts">
	import { buildBlogPostingSchema, buildPageSeo, renderJsonLd } from '$lib/seo';
	import { absoluteUrl, site } from '$lib/site';
	import type { PageData } from './$types';

	let { data }: { data: PageData } = $props();
	const seo = $derived(
		buildPageSeo({
			title: data.post.title,
			description: data.post.description,
			path: `/writing/${data.post.slug}`,
			type: 'article',
			keywords: data.post.tags,
			publishedTime: data.post.date
		})
	);
	const articleSchema = $derived(renderJsonLd(buildBlogPostingSchema(data.post)));
</script>

<svelte:head>
	<title>{seo.title}</title>
	<meta name="description" content={seo.description} />
	<meta name="author" content={site.author} />
	<meta name="keywords" content={seo.keywords} />
	<link rel="canonical" href={seo.canonical} />
	<link rel="alternate" type="application/rss+xml" title={`${site.name} RSS`} href={absoluteUrl('/rss.xml')} />
	<meta property="og:type" content={seo.type} />
	<meta property="og:title" content={seo.title} />
	<meta property="og:description" content={seo.description} />
	<meta property="og:url" content={seo.canonical} />
	<meta property="og:image" content={seo.image} />
	<meta property="og:site_name" content={site.name} />
	<meta property="article:published_time" content={seo.publishedTime} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seo.title} />
	<meta name="twitter:description" content={seo.description} />
	<meta name="twitter:image" content={seo.image} />
	{@html articleSchema}
</svelte:head>

<article class="page-shell prose">
	<header class="page-header">
		<div class="page-header__top">
			<a class="header-link" href="/">Writing</a>
		</div>
		<h1>{data.post.title}</h1>
		<div class="post-card__meta">
			<time datetime={data.post.date}>{data.post.formattedDate}</time>
			<ul class="tag-list" aria-label={`Tags for ${data.post.title}`}>
				{#each data.post.tags as tag}
					<li>{tag}</li>
				{/each}
			</ul>
		</div>
		<p class="intro">{data.post.description}</p>
	</header>

	<section class="prose-body">
		<data.post.component />
	</section>

	<footer class="site-footer">
		<a href="/projects">Projects</a>
		<a href="/guides">Guides</a>
		<a href="/">Back</a>
		<a href="/rss.xml">RSS</a>
	</footer>
</article>

<style>
	.page-shell {
		width: min(var(--measure-page), calc(100% - var(--space-2xl)));
		margin: 0 auto;
		min-height: 100svh;
		display: flex;
		flex-direction: column;
		padding: calc(var(--space-4xl) + var(--space-sm)) 0 var(--space-5xl);
	}

	.page-header {
		display: grid;
		gap: var(--space-md);
		padding-bottom: calc(var(--space-3xl) + var(--space-sm));
	}

	.header-link {
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.post-card__meta {
		display: grid;
		align-content: start;
		gap: var(--space-sm);
		font-size: var(--text-sm);
		color: var(--color-soft);
	}

	.post-card__meta time {
		font-family: var(--font-mono);
		font-size: 0.78rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-accent);
		font-weight: 600;
	}

	.tag-list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.3rem;
	}

	.tag-list li {
		padding: 0;
		font-size: var(--text-meta);
		color: var(--color-faint);
	}

	.tag-list li::after {
		content: ',';
		margin-left: var(--space-2xs);
		color: var(--color-soft);
	}

	.tag-list li:last-child::after {
		content: '';
		margin: 0;
	}

	.intro {
		max-width: var(--measure-intro-wide);
		font-size: 1.04rem;
		line-height: 1.7;
		color: var(--color-muted);
		font-style: italic;
	}

	.prose {
		position: relative;
		font-size: var(--text-body);
		line-height: var(--leading-prose);
	}

	.prose-body {
		width: min(100%, var(--measure-prose));
		font-family: var(--font-prose);
		font-size: 1.22rem;
		line-height: 1.88;
	}

	.prose-body > :global(* + *) {
		margin-top: var(--space-lg);
	}

	.prose-body :global(p:first-child) {
		margin-bottom: 0;
		font-size: 1.32rem;
		line-height: 1.72;
		color: var(--color-ink);
	}

	.prose-body :global(h2),
	.prose-body :global(h3),
	.prose-body :global(h4) {
		font-family: var(--font-display);
		color: var(--color-ink);
	}

	.prose-body :global(h2) {
		margin-top: calc(var(--space-3xl) + var(--space-sm));
		margin-bottom: var(--space-sm);
		font-size: 2.15rem;
		line-height: var(--leading-tight);
		letter-spacing: -0.03em;
	}

	.prose-body :global(h3) {
		margin-top: var(--space-xl);
		margin-bottom: var(--space-xs);
		font-size: 1.55rem;
		line-height: 1.12;
	}

	.prose-body :global(h4) {
		margin-top: var(--space-lg);
		margin-bottom: 0.25rem;
		font-size: 1.1rem;
		line-height: 1.2;
	}

	.prose-body :global(p),
	.prose-body :global(li) {
		color: var(--color-ink-2);
	}

	.prose-body :global(p + p) {
		margin-top: var(--space-md);
	}

	.prose-body :global(strong) {
		color: var(--color-ink);
		font-weight: 650;
	}

	.prose-body :global(blockquote) {
		margin: var(--space-xl) 0;
		padding: 0 0 0 var(--space-lg);
		border-left: var(--border-strong);
		font-family: var(--font-body);
		font-size: 0.98rem;
		line-height: 1.65;
		color: var(--color-muted);
	}

	.prose-body :global(ul),
	.prose-body :global(ol) {
		margin: var(--space-lg) 0;
		padding-left: 1.2rem;
		list-style: revert;
	}

	.prose-body :global(li + li) {
		margin-top: 0.55rem;
	}

	.prose-body :global(a) {
		text-decoration-color: currentColor;
	}

	.prose-body :global(hr) {
		margin: var(--space-2xl) 0;
		border: 0;
		border-top: var(--border-subtle);
	}

	.prose-body :global(code) {
		padding: 0.1rem 0.32rem;
		background: var(--color-code);
		font-family: 'SF Mono', 'Menlo', monospace;
		font-size: 0.88em;
	}

	.prose-body :global(pre) {
		overflow-x: auto;
		margin: var(--space-xl) 0;
		padding: var(--space-lg);
		border-radius: var(--radius-md);
		background: var(--color-code-block);
	}

	.prose-body :global(pre code) {
		padding: 0;
		background: transparent;
		font-size: 0.9rem;
		line-height: 1.7;
	}

	.prose-body :global(img) {
		width: 100%;
		margin: var(--space-xl) 0;
	}

	.prose-body :global(table) {
		width: 100%;
		border-collapse: collapse;
		font-family: var(--font-body);
		font-size: 0.98rem;
	}

	.prose-body :global(th),
	.prose-body :global(td) {
		padding: 0.55rem 0;
		border-bottom: 1px solid var(--color-border);
		text-align: left;
		vertical-align: top;
	}

	.site-footer {
		display: flex;
		gap: var(--space-lg);
		margin-top: auto;
		padding-top: calc(var(--space-4xl) + var(--space-sm));
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-muted);
	}

	@media (max-width: 640px) {
		.page-shell {
			padding-top: var(--space-xl);
			padding-bottom: var(--space-4xl);
		}

		.page-header {
			padding-bottom: 1.7rem;
		}

		.prose {
			padding-bottom: var(--space-5xl);
		}

		.site-footer {
			padding-top: var(--space-4xl);
		}

		.prose-body {
			font-size: var(--text-prose-sm);
			line-height: var(--leading-prose-sm);
		}

		.prose-body :global(p:first-child) {
			font-size: 1.16rem;
		}

		.prose-body :global(h2) {
			margin-top: var(--space-2xl);
			font-size: 1.8rem;
		}
	}
</style>
